import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import HideImageIcon from "@mui/icons-material/HideImage";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import { LogoLoader } from "../../components/ui";

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const ImageContainer = styled(CardMedia)(({ theme }) => ({
  height: 200,
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'default',
  position: 'relative',
  overflow: 'hidden',
}));



const CategoryHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const NomineePhotos = ({
  memberId,
  nomineeId,
  nomineePhotosData,
  setNomineePhotosData,
  nomineeDetailsData,
  handleNext,
  type,
  // Disbursed loan: fields are already read-only via type="View"; accepted
  // here so the prop is declared rather than leaking to the DOM.
  locked = false,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [photos, setPhotos] = useState(nomineePhotosData || {});
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");

  // Sync photos when nomineePhotosData prop changes (populated by parent)
  useEffect(() => {
    if (nomineePhotosData && Object.keys(nomineePhotosData).length > 0) {
      setPhotos(nomineePhotosData);
    }
  }, [nomineePhotosData]);

  // Also fetch directly by memberId as a fallback
  useEffect(() => {
    const fetchPhotos = async () => {
      if (!memberId) return;

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/getModelData/nominee_photos?filter={"memberId":${memberId}}`,
          { withCredentials: true }
        );

        const data = response.data.data[0] || {};
        if (data && Object.keys(data).length > 0) {
          setPhotos(data);
          setNomineePhotosData(data);
        }
      } catch (error) {
        console.error("Error fetching nominee photos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhotos();
  }, [memberId]);

  const handleImageClick = (imagePath, title) => {
    if (imagePath) {
      setSelectedImage(`${API_BASE_URL}/images/${imagePath}`);
      setSelectedTitle(title);
      setOpenDialog(true);
    }
  };

  const getPhoto = (name) => {
    if (photos && photos[name]) return photos[name];
    
    // nomineeDetailsData fields mapping
    if (name === "anotherIdentity") return nomineeDetailsData?.idProof;
    if (name === "anotherIdentityPhoto") return nomineeDetailsData?.idProofFrontImage;
    if (name === "aadharFrontPhoto") return nomineeDetailsData?.aadharFrontImage;
    if (name === "aadharBackPhoto") return nomineeDetailsData?.aadharBackImage;
    
    return null;
  };

  const photoCategories = [
    {
      title: "Personal Information",
      color: "#1976d2",
      items: [
        { label: "Nominee Photo", name: "nomineePhoto" },
        { label: "Signature", name: "signaturePhoto" },
        { label: "Another Identity", name: "anotherIdentity", isText: true },
      ]
    },
    {
      title: "Identity Documents",
      color: "#2e7d32",
      items: [
        { label: "Aadhar Front", name: "aadharFrontPhoto" },
        { label: "Aadhar Back", name: "aadharBackPhoto" },
        { label: "Another Identity Photo", name: "anotherIdentityPhoto" },
      ]
    },
    {
      title: "Financial Documents",
      color: "#ed6c02",
      items: [
        { label: "Bank Passbook", name: "bankPassbookPhoto" },
        { label: "Salary Proof", name: "salaryProofPhoto" },
      ]
    },
    {
      title: "Additional Documents",
      color: "#9c27b0",
      items: [
        { label: "House Proof", name: "ownHouseProofPhoto" },
        { label: "Applicant Link Proof", name: "applicantLinkProofPhoto" },
        { label: "Ration Card", name: "rationCardPhoto" },
        { label: "Other Document 1", name: "other1" },
        { label: "Other Document 2", name: "other2" },
        { label: "Other Document 3", name: "other3" },
      ]
    },
  ];

  if (isLoading && !photos.id) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LogoLoader size="lg" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom fontWeight="500">Nominee Documents</Typography>
            <Typography variant="body1" color="text.secondary">Member ID: {memberId} | Nominee ID: {nomineeId}</Typography>
          </Box>
          <Chip label="View Mode" color="default" />
        </Box>
      </Paper>

      {errorMessage && <Alert severity="error" sx={{ mb: 4 }}>{errorMessage}</Alert>}

      {photoCategories.map((category, index) => (
        <Box key={index} mb={4}>
          <CategoryHeader>
            <Box sx={{ width: 4, height: 24, bgcolor: category.color, borderRadius: 1, mr: 2 }} />
            <Typography variant="h5" fontWeight="500">{category.title}</Typography>
          </CategoryHeader>
          <Grid container spacing={3}>
            {category.items.map((item, itemIndex) => (
              <Grid item xs={12} sm={6} md={4} key={itemIndex}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="500" gutterBottom>{item.label}</Typography>
                  </CardContent>
                  
                  {item.isText ? (
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="body1">{getPhoto(item.name) || "Not provided"}</Typography>
                    </CardContent>
                  ) : (
                    <ImageContainer
                      onClick={() => handleImageClick(getPhoto(item.name), item.label)}
                      sx={{ cursor: getPhoto(item.name) ? 'pointer' : 'default' }}
                    >
                      {getPhoto(item.name) ? (
                        <img
                          src={`${API_BASE_URL}/images/${getPhoto(item.name)}`}
                          alt={item.label}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box textAlign="center" color="text.secondary">
                          <HideImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                          <Typography variant="body2" color="text.disabled">No image uploaded</Typography>
                        </Box>
                      )}
                    </ImageContainer>
                  )}
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Box mt={4} display="flex" justifyContent="space-between">
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={() => navigate("/addEditBusinessLoan", { state: { type, stepNo: 6, memberId } })}
        >
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<NavigateNextIcon />}
          onClick={handleNext}
        >
          Continue
        </Button>
      </Box>

      {/* Preview Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedTitle}
          <IconButton onClick={() => setOpenDialog(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            {selectedImage && <img src={selectedImage} alt={selectedTitle} style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />}
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default NomineePhotos;
